import { useEffect, useMemo, useState, useRef } from 'react';
import { useAppSelector } from '../../hooks/storeHooks';
import Helpers from '../../utilities/Helpers';
import TableToolbar from '../data-tables/TableToolbar';
import RealTimeDataTable, { ColumnFields } from '../data-tables/RealTimeDataTable';
import * as XLSX from 'xlsx';
import { formatDate } from '../../utilities/dateHelpers';

interface RealTimeDataProps {
  rPermission: any;
}
const RealTimeData: React.FC<RealTimeDataProps> = ({ rPermission }) => {
  const assetTrendData = useAppSelector((state: any) => state.AssetTrendDataSlice.rawData);
  const loading = useAppSelector((state: any) => state.AssetTrendDataSlice.loading);
  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const [visibleTableData, setVisibleTableData] = useState<any[]>([]);
  const [rawSearchTerm, setRawSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeseriesChartData = useMemo(() => {
    return pumpCheckerPageData?.find((obj: any) => obj.key === 'timeseriesChart');
  }, [pumpCheckerPageData]);
  const nameShortNameMapping: Record<string, string> = {};
  const processedAssetTrendData = useMemo(() => {
    if (!assetTrendData || assetTrendData.length === 0) return [];

    const dateMap: Record<string, any> = {};
    assetTrendData.forEach((trend: { dataPoints: any[]; trendName: string | number; shortName: string }) => {
      if (!trend.dataPoints || trend.dataPoints.length === 0) return;
      nameShortNameMapping[trend.trendName] = trend.shortName;
      trend.dataPoints.forEach((dataPoint: any) => {
        const date = dataPoint.date;
        if (!dateMap[date]) {
          dateMap[date] = { date };
        }
        dateMap[date][trend.trendName] = dataPoint.value ?? '';
      });
    });

    // Convert the date map to an array and sort by date in ascending order
    const sortedData = Object.values(dateMap).sort((a: any, b: any) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });

    return sortedData;
  }, [assetTrendData]);

  const columnFields: ColumnFields[] = useMemo(() => {
    if (!timeseriesChartData?.widgetProperty?.widgetProperties?.parameterConfig) {
      return [];
    }

    const parameterConfig = timeseriesChartData.widgetProperty.widgetProperties.parameterConfig;
    const parameters = parameterConfig[0]?.parameters || [];
    const fixedDateColumn = { name: 'date', displayOrder: '0', units: '', shortName: 'Date / Time' };

    return [
      fixedDateColumn,
      ...parameters
        .filter(
          (param: { name: any; displayOrder: any; selected: boolean }) =>
            param?.name && param?.displayOrder && param.selected,
        )
        .sort(
          (a: { displayOrder: string }, b: { displayOrder: string }) =>
            (parseInt(a.displayOrder) || 0) - (parseInt(b.displayOrder) || 0),
        )
        .map((param: { name: any; displayOrder: any; units: any; shortName: string; selected: boolean }) => ({
          name: param.name || '',
          displayOrder: param.displayOrder || '',
          units: nameShortNameMapping[param.name]?.match(/\(([^)]+)\)/)?.[1] || param?.units || '',
          shortName: Helpers.toSentenceCase(
            (nameShortNameMapping[param.name]?.replace(/\s*\(.*?\)\s*/g, '').trim() || param?.name || '') as string,
          ),
          isVisible: param.selected,
        })),
    ];
  }, [timeseriesChartData, processedAssetTrendData]);

  const handleSearch = (searchTerm: string) => {
    setRawSearchTerm(searchTerm);
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(rawSearchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [rawSearchTerm]);

  // Cleanup: Clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleExportXLS = () => {
    if (!visibleTableData || visibleTableData.length === 0) {
      console.warn('No data available for export');
      return;
    }
    const headerRow = columnFields.map((col) => {
      const baseName = col.shortName || col.name;
      return col.units ? `${baseName} (${col.units})` : baseName;
    });
    const worksheetData = [
      headerRow,
      ...visibleTableData.map((row: any) => {
        return columnFields.map((col) => {
          const value = row[col.name];
          if (col.name.toLowerCase() === 'date' && value) {
            const formatted = formatDate(value, true);
            return formatted !== '-' ? formatted : '-';
          }
          if (!isNaN(Number(value)) && value !== null) {
            const num = Number(value);
            if (num % 1 !== 0) {
              return (Math.floor(num * 1000) / 1000).toFixed(3).replace(/\.?0+$/, ''); // Truncate to 3 decimals
            }
            return num.toString();
          }
          return value ?? '-';
        });
      }),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RealTime Data');
    worksheet['!cols'] = columnFields.map(() => ({ wch: 20 }));
    XLSX.writeFile(workbook, `RealTimeData-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div>
      <TableToolbar
        columns={columnFields.map((col) => ({
          id: col.displayOrder,
          Header: col.shortName || col.name,
          isVisible: col.isVisible,
        }))}
        onSearch={handleSearch}
        onExportXLS={() => handleExportXLS()}
        onColumnToggle={(columnId: string) => console.log('Toggle column:', columnId)}
        onSaveColumns={() => console.log('Save Columns')}
        onUpdateColumns={(updated) => console.log('Update columns:', updated)}
        onApplyClicked={() => console.log('Apply Columns')}
        saveConfigHasbeenClicked={(config) => console.log('Save config:', config)}
        disableAllInstallations={true}
        disableColumns={true}
        ref={undefined}
        rPermission={rPermission}
        tableName='Time Data'
      />
      <RealTimeDataTable
        searchTerm={debouncedSearchTerm}
        data={processedAssetTrendData}
        columns={columnFields}
        loading={loading}
        onFilteredDataReady={setVisibleTableData}
      />
    </div>
  );
};

export default RealTimeData;
